// Insurance Estimator Constants - Rates, Formulas, and Configuration
// Based on PRD-insurance-estimator.md

export const MITIGATION_RATES = {
  dehumidifier: { default: 95, min: 65, max: 150, unit: 'per unit/day', label: 'LGR Dehumidifier (180 PPD)' },
  airMover: { default: 40, min: 25, max: 65, unit: 'per unit/day', label: 'Air Mover' },
  extraction: { default: 0.75, min: 0.50, max: 1.25, unit: 'per SF', label: 'Water Extraction' },
  monitoring: { default: 175, min: 100, max: 250, unit: 'per visit', label: 'Moisture Monitoring Visit' },
  delivery: { default: 125, min: 75, max: 200, unit: 'per trip', label: 'Equipment Delivery' },
  emergency: { default: 350, min: 250, max: 500, unit: 'flat', label: 'Emergency Response' },
  containment: { default: 2.50, min: 1.50, max: 4.00, unit: 'per LF', label: 'Containment (Poly)' },
  antimicrobial: { default: 0.35, min: 0.25, max: 0.60, unit: 'per SF', label: 'Antimicrobial Application' },
  contentManip: { default: 275, min: 150, max: 400, unit: 'flat', label: 'Content Manipulation' },
  ozone: { default: 85, min: 50, max: 125, unit: 'per unit/day', label: 'Ozone Machine' },
  hydroxyl: { default: 95, min: 60, max: 140, unit: 'per unit/day', label: 'Hydroxyl Generator' },
  thermalImaging: { default: 150, min: 100, max: 250, unit: 'per visit', label: 'Thermal Imaging Survey' },
  injectidry: { default: 125, min: 75, max: 175, unit: 'per unit/day', label: 'Injectidry System' },
  hepaAirScrubber: { default: 75, min: 50, max: 125, unit: 'per unit/day', label: 'HEPA Air Scrubber' },
  negativeAir: { default: 85, min: 50, max: 125, unit: 'per unit/day', label: 'Negative Air Machine' },
  ppeSupplies: { default: 125, min: 75, max: 200, unit: 'flat', label: 'PPE/Supplies' },
};

export const WATER_CATEGORIES = {
  cat1: { label: 'Category 1', description: 'Clean water from supply lines', adjustment: 0 },
  cat2: { label: 'Category 2', description: 'Gray water from appliances', adjustment: 0.30 },
  cat3: { label: 'Category 3', description: 'Black water (sewage, flood)', adjustment: 0.75 },
};

export const LABOR_SPLITS = {
  demolition: { labor: 85, label: 'Demolition' },
  drywall: { labor: 55, label: 'Drywall' },
  texture: { labor: 70, label: 'Texture' },
  paint: { labor: 65, label: 'Paint' },
  flooringLvp: { labor: 40, label: 'Flooring - LVP' },
  flooringCarpet: { labor: 35, label: 'Flooring - Carpet' },
  flooringTile: { labor: 50, label: 'Flooring - Tile' },
  flooringHardwood: { labor: 40, label: 'Flooring - Hardwood' },
  trim: { labor: 50, label: 'Trim/Baseboard' },
  casing: { labor: 55, label: 'Door Casing' },
  cabinets: { labor: 35, label: 'Cabinets' },
  plumbing: { labor: 70, label: 'Plumbing' },
  electrical: { labor: 75, label: 'Electrical' },
  cleaning: { labor: 90, label: 'Cleaning' },
  general: { labor: 50, label: 'General/Other' },
};

export const FORMULAS = {
  dehuPerSF: 500, // 1 dehumidifier per 500 SF
  airMoverMultiplier: 2.5, // Air movers per dehu
  airMoverBuffer: 2, // Additional air movers
  oAndPPercent: 20, // Overhead & Profit percentage
  containmentMultiplier: 4, // SQRT(SF) * 4 for containment LF
};

export const DRYING_DAYS_GUIDANCE = [
  { range: '3-5', description: 'Standard losses' },
  { range: '6-10', description: 'Heavy losses' },
  { range: '10-14', description: 'Severe/structural' },
];

export const GLOSSARY = {
  lgr: {
    term: 'LGR',
    definition: 'Low Grain Refrigerant dehumidifier - commercial grade',
    whyItMatters: 'Insurance expects commercial equipment',
  },
  cat1: {
    term: 'Category 1',
    definition: 'Clean water from supply lines',
    whyItMatters: 'Base rate, minimal protocols',
  },
  cat2: {
    term: 'Category 2',
    definition: 'Gray water from appliances',
    whyItMatters: 'Requires antimicrobial, higher rate',
  },
  cat3: {
    term: 'Category 3',
    definition: 'Black water (sewage, flood)',
    whyItMatters: 'Extensive protocols, highest rate',
  },
  oAndP: {
    term: 'O&P',
    definition: 'Overhead (10%) + Profit (10%)',
    whyItMatters: 'Standard, expected by insurance',
  },
  antimicrobial: {
    term: 'Antimicrobial',
    definition: 'Treatment preventing mold/bacteria',
    whyItMatters: 'Standard practice, liability if skipped',
  },
  iicrcs500: {
    term: 'IICRC S500',
    definition: 'Industry standard for water damage',
    whyItMatters: 'Cite when adjusters push back',
  },
  dryingDays: {
    term: 'Drying Days',
    definition: 'Days equipment runs',
    whyItMatters: '3-5 standard, up to 14 for severe',
  },
  monitoring: {
    term: 'Monitoring',
    definition: 'Daily moisture readings',
    whyItMatters: 'Documents drying, justifies equipment days',
  },
};

export const ADJUSTER_SCRIPTS = {
  tooManyDehus: {
    objection: 'Too many dehumidifiers',
    response: 'Per IICRC S500, the standard is one LGR dehumidifier per 100-150 SF of affected area. With {sqft} SF affected, {dehuCount} units is within industry standards for optimal drying time.',
  },
  excessiveDryingDays: {
    objection: 'Drying days are excessive',
    response: 'Our moisture readings show the structure didn\'t reach dry standard until day {dryingDays}. Daily monitoring logs document the moisture levels. Removing equipment early risks mold growth and additional damage.',
  },
  monitoringCharges: {
    objection: 'Why charge for monitoring visits?',
    response: 'Daily monitoring is industry standard practice per IICRC S500. Each visit includes moisture readings at multiple points, equipment checks, and documentation. This creates the evidence trail supporting the equipment days claimed.',
  },
  antimicrobialUnnecessary: {
    objection: 'Antimicrobial wasn\'t necessary',
    response: 'This was {category} water. Per IICRC S500, antimicrobial application is standard protocol for Category 2+ losses to prevent microbial amplification. Skipping creates liability for future mold issues.',
  },
};

// Calculate equipment recommendations based on square footage
export function calculateEquipmentRecommendations(sqft) {
  const dehuCount = Math.ceil(sqft / FORMULAS.dehuPerSF);
  const airMoverCount = Math.ceil(dehuCount * FORMULAS.airMoverMultiplier) + FORMULAS.airMoverBuffer;
  const containmentLF = Math.ceil(Math.sqrt(sqft) * FORMULAS.containmentMultiplier);

  return {
    dehumidifiers: dehuCount,
    airMovers: airMoverCount,
    containmentLF,
  };
}

// Calculate mitigation line items based on inputs
export function calculateMitigationLineItems(values, rates) {
  const items = [];

  // Emergency Response
  if (values.afterHours) {
    items.push({
      id: 'emergency',
      description: 'Emergency Response (After Hours)',
      qty: 1,
      unit: 'EA',
      rate: rates.emergency || MITIGATION_RATES.emergency.default,
      total: rates.emergency || MITIGATION_RATES.emergency.default,
      laborPct: 100,
    });
  }

  // Water Extraction
  items.push({
    id: 'extraction',
    description: 'Water Extraction',
    qty: values.sqft,
    unit: 'SF',
    rate: rates.extraction || MITIGATION_RATES.extraction.default,
    total: values.sqft * (rates.extraction || MITIGATION_RATES.extraction.default),
    laborPct: 85,
  });

  // Dehumidifiers
  const dehuDays = values.dehumidifiers * values.dryingDays;
  items.push({
    id: 'dehumidifier',
    description: `LGR Dehumidifier (${values.dehumidifiers} x ${values.dryingDays} days)`,
    qty: dehuDays,
    unit: 'DAYS',
    rate: rates.dehumidifier || MITIGATION_RATES.dehumidifier.default,
    total: dehuDays * (rates.dehumidifier || MITIGATION_RATES.dehumidifier.default),
    laborPct: 0,
  });

  // Air Movers
  const airMoverDays = values.airMovers * values.dryingDays;
  items.push({
    id: 'airMover',
    description: `Air Mover (${values.airMovers} x ${values.dryingDays} days)`,
    qty: airMoverDays,
    unit: 'DAYS',
    rate: rates.airMover || MITIGATION_RATES.airMover.default,
    total: airMoverDays * (rates.airMover || MITIGATION_RATES.airMover.default),
    laborPct: 0,
  });

  // Monitoring
  items.push({
    id: 'monitoring',
    description: 'Moisture Monitoring',
    qty: values.monitoringVisits,
    unit: 'EA',
    rate: rates.monitoring || MITIGATION_RATES.monitoring.default,
    total: values.monitoringVisits * (rates.monitoring || MITIGATION_RATES.monitoring.default),
    laborPct: 100,
  });

  // Delivery/Pickup
  items.push({
    id: 'delivery',
    description: 'Equipment Delivery/Pickup',
    qty: 2,
    unit: 'EA',
    rate: rates.delivery || MITIGATION_RATES.delivery.default,
    total: 2 * (rates.delivery || MITIGATION_RATES.delivery.default),
    laborPct: 100,
  });

  // Containment
  const recommendations = calculateEquipmentRecommendations(values.sqft);
  items.push({
    id: 'containment',
    description: 'Containment (Poly Barrier)',
    qty: recommendations.containmentLF,
    unit: 'LF',
    rate: rates.containment || MITIGATION_RATES.containment.default,
    total: recommendations.containmentLF * (rates.containment || MITIGATION_RATES.containment.default),
    laborPct: 70,
  });

  // Antimicrobial
  items.push({
    id: 'antimicrobial',
    description: 'Antimicrobial Application',
    qty: values.sqft,
    unit: 'SF',
    rate: rates.antimicrobial || MITIGATION_RATES.antimicrobial.default,
    total: values.sqft * (rates.antimicrobial || MITIGATION_RATES.antimicrobial.default),
    laborPct: 75,
  });

  // Content Manipulation
  items.push({
    id: 'contentManip',
    description: 'Content Manipulation',
    qty: 1,
    unit: 'EA',
    rate: rates.contentManip || MITIGATION_RATES.contentManip.default,
    total: rates.contentManip || MITIGATION_RATES.contentManip.default,
    laborPct: 90,
  });

  // Category adjustment
  const categoryAdjustment = WATER_CATEGORIES[values.waterCategory]?.adjustment || 0;
  if (categoryAdjustment > 0) {
    items.push({
      id: 'categoryAdjustment',
      description: `${WATER_CATEGORIES[values.waterCategory].label} Protocol Adjustment`,
      qty: values.sqft,
      unit: 'SF',
      rate: categoryAdjustment,
      total: values.sqft * categoryAdjustment,
      laborPct: 75,
    });
  }

  return items;
}

// Default rates object
export function getDefaultRates() {
  const rates = {};
  Object.entries(MITIGATION_RATES).forEach(([key, config]) => {
    rates[key] = config.default;
  });
  return rates;
}

// Default mitigation values
export function getDefaultMitigationValues() {
  return {
    sqft: 0,
    dehumidifiers: 0,
    airMovers: 0,
    dryingDays: 4,
    monitoringVisits: 4,
    afterHours: false,
    waterCategory: 'cat2',
  };
}

// Empty estimate template
export function createEmptyEstimate() {
  return {
    status: 'draft',
    propertyAddress: '',
    estimateName: '',
    currentStep: 1,
    upload: null,
    mitigation: {
      approvals: {
        sqft: false,
        dehumidifiers: false,
        airMovers: false,
      },
      values: getDefaultMitigationValues(),
      rates: getDefaultRates(),
      lineItems: [],
      subtotal: 0,
    },
    repairs: {
      items: [],
      subtotal: 0,
      laborTotal: 0,
      materialTotal: 0,
    },
    totals: {
      mitigation: 0,
      repairs: 0,
      subtotal: 0,
      oAndP: 0,
      oAndPPercent: FORMULAS.oAndPPercent,
      grandTotal: 0,
    },
    documentation: {
      damagePhotos: false,
      equipmentPhotos: false,
      moistureLogs: false,
      completionPhotos: false,
    },
    clarificationThread: [],
    versions: [],
    currentVersion: 0,
  };
}
